import {
  a,
  h1,
  h2,
  main,
  p,
  section,
  type Mountable,
  type Send,
  type Signal,
} from '@llui/dom';

import { t } from './i18n.js';

/**
 * `/recovery` — GM recovery / backup posture explainer (#89).
 *
 * A longer plain-language counterpart to the one-time disclaimer modal:
 * where your data lives, why export is your backup, what's coming
 * (point-in-time snapshots), and that deletion is permanent. Reachable
 * without a session — the disclaimer modal's "Read more about recovery"
 * link points here, and the route shell doesn't gate routes at Phase 1
 * scope (mirrors `/a11y`).
 *
 * Layout mirrors `a11y-page.ts`: a centred reading column with an `h1`
 * page title and `<section>`s with `h2` headings, reusing the
 * `a11y-page` / `a11y-section` card styling so the two long-form prose
 * surfaces stay visually consistent. Every string flows through `t()`.
 *
 * Generic over `Model, Msg` so it mounts into the TEA loop without
 * binding to any model slice (the content is static prose).
 */
export function viewRecoveryPage<Model, Msg>(
  _state: Signal<Model>,
  _send: Send<Msg>,
): Mountable {
  return main(
    { role: 'main', 'data-testid': 'recovery-page', class: 'a11y-page recovery-page' },
    [
      h1({ class: 'a11y-title' }, [t('recovery.heading')]),
      p({ class: 'a11y-intro', 'data-testid': 'recovery-intro' }, [t('recovery.intro')]),

      section({ class: 'a11y-section', id: 'recovery-where' }, [
        h2([t('recovery.where_heading')]),
        p([t('recovery.where_body')]),
      ]),

      section({ class: 'a11y-section', id: 'recovery-export' }, [
        h2([t('recovery.export_heading')]),
        p([t('recovery.export_body')]),
      ]),

      section({ class: 'a11y-section', id: 'recovery-snapshots' }, [
        h2([t('recovery.snapshots_heading')]),
        p([t('recovery.snapshots_body')]),
      ]),

      section({ class: 'a11y-section', id: 'recovery-deletion' }, [
        h2([t('recovery.deletion_heading')]),
        p([t('recovery.deletion_body')]),
      ]),

      section({ class: 'a11y-section', id: 'recovery-back' }, [
        p({ class: 'a11y-contact-cta' }, [
          a({ class: 'a11y-contact-link', href: '/', 'data-testid': 'recovery-back-link' }, [
            t('recovery.back_to_app'),
          ]),
        ]),
      ]),
    ],
  );
}
